import type {
  ActionSpec,
  AppProfile,
  AppSpec,
  AppStruct,
  ConnectorSpec,
  ConnectorStruct,
  PerkIntegrationSpec,
  TriggerIntegrationSpec,
} from '@cohort/shared/schema/apps/app';
import {AppProfileSchema, AppProfileWithIdSchema} from '@cohort/shared/schema/apps/app';
import {CreateRoleActionSpec} from '@cohort/shared/schema/apps/discord/actions/createRole';
import {GetMemberActionSpec} from '@cohort/shared/schema/apps/discord/actions/getMember';
import {InviteToChannelActionSpec} from '@cohort/shared/schema/apps/discord/actions/inviteToChannel';
import {ListRolesActionSpec} from '@cohort/shared/schema/apps/discord/actions/listRoles';
import {RemoveRoleActionSpec} from '@cohort/shared/schema/apps/discord/actions/removeRole';
import {DiscordPrivateAccessPerkIntegrationSpec} from '@cohort/shared/schema/apps/discord/perks/privateAccess';
import {DiscordHasRoleTriggerIntegrationSpec} from '@cohort/shared/schema/apps/discord/triggers/hasRole';
import type {OAuth2Credentials} from '@cohort/shared/schema/apps/oauth';
import {
  OAuth2AppCredentialsSchema,
  OAuth2CredentialsSchema,
} from '@cohort/shared/schema/apps/oauth';
import {z} from 'zod';

export const DISCORD_APP_ID = 'discord' as const;

export const DiscordAppCredentialsSchema = OAuth2AppCredentialsSchema.extend({
  botToken: z.string(),
});
export type DiscordAppCredentials = z.infer<typeof DiscordAppCredentialsSchema>;

export const DiscordAppProfileSchema = AppProfileWithIdSchema.extend({
  username: z.string(),
  discriminator: z.string(),
});
export type DiscordAppProfile = z.infer<typeof DiscordAppProfileSchema>;

export type DiscordMerchantConnectorStruct = ConnectorStruct<
  'discord-merchant',
  DiscordAppCredentials,
  DiscordMerchantCredentials,
  AppProfile,
  null
>;

export type DiscordUserConnectorStruct = ConnectorStruct<
  'discord',
  DiscordAppCredentials,
  OAuth2Credentials,
  DiscordAppProfile,
  null
>;

export const DiscordUserConnectorSpec = {
  id: 'discord',
  type: 'oauth2',
  appCredentialsSchema: DiscordAppCredentialsSchema,
  credentialsSchema: OAuth2CredentialsSchema,
  profileSchema: DiscordAppProfileSchema,
  connectionConfigSchema: null,
} satisfies ConnectorSpec<DiscordUserConnectorStruct>;

export const DiscordMerchantCredentialsSchema = OAuth2CredentialsSchema.extend({
  guildId: z.string(),
});
export type DiscordMerchantCredentials = z.infer<typeof DiscordMerchantCredentialsSchema>;

export const DiscordMerchantConnectorSpec = {
  id: 'discord-merchant',
  type: 'oauth2',
  appCredentialsSchema: DiscordAppCredentialsSchema,
  credentialsSchema: DiscordMerchantCredentialsSchema,
  profileSchema: AppProfileSchema,
  connectionConfigSchema: null,
} satisfies ConnectorSpec<DiscordMerchantConnectorStruct>;

export const DiscordOauth2CallbackParamsSchema = z.object({
  guild_id: z.string(),
});
export type DiscordOauth2CallbackParams = z.infer<typeof DiscordOauth2CallbackParamsSchema>;

export const DiscordPerkIntegrationSpecs = [
  DiscordPrivateAccessPerkIntegrationSpec,
] as const satisfies ReadonlyArray<PerkIntegrationSpec>;
type DiscordPerkIntegrationIds = (typeof DiscordPerkIntegrationSpecs)[number]['id'];

export const DiscordTriggerIntegrationSpecs = [
  DiscordHasRoleTriggerIntegrationSpec,
] as const satisfies ReadonlyArray<TriggerIntegrationSpec>;
type DiscordTriggerIntegrationIds = (typeof DiscordTriggerIntegrationSpecs)[number]['id'];

export const DiscordActionSpecs = [
  CreateRoleActionSpec,
  GetMemberActionSpec,
  InviteToChannelActionSpec,
  ListRolesActionSpec,
  RemoveRoleActionSpec,
] as const satisfies ReadonlyArray<ActionSpec>;
type DiscordActionIds = (typeof DiscordActionSpecs)[number]['id'];

export type DiscordAppStruct = AppStruct<
  'discord',
  DiscordMerchantConnectorStruct,
  DiscordUserConnectorStruct,
  null,
  DiscordActionIds,
  never,
  DiscordPerkIntegrationIds,
  DiscordTriggerIntegrationIds,
  never,
  never
>;
export const DiscordAppSpec: AppSpec<DiscordAppStruct> = {
  id: 'discord',
  name: 'Discord',
  merchantConnector: DiscordMerchantConnectorSpec,
  userConnector: DiscordUserConnectorSpec,
  actionSpecs: DiscordActionSpecs,
  usersSyncSpec: null,
  notificationIntegrationSpecs: [],
  perkIntegrationSpecs: DiscordPerkIntegrationSpecs,
  triggerIntegrationSpecs: DiscordTriggerIntegrationSpecs,
  userEventSpecs: [],
  resourceSpecs: [],
};
